import React, { useMemo } from 'react';
import {
    ResponsiveContainer,
    ComposedChart,
    XAxis,
    YAxis,
    CartesianGrid,
    ReferenceLine,
} from 'recharts';
import { Card, Typography, useTheme, useMediaQuery } from '@mui/material';
import dayjs from 'dayjs';

const formatRealHour = (v) =>
    new Date(v * 1000).toLocaleTimeString('pt-BR', { hour12: false });

function ColorTimelineChart({ chart, height = 50 }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const apiTimeline = useMemo(() => chart?.meta?.colorTimeline ?? [], [chart]);

    const data = useMemo(() => {
        if (!Array.isArray(apiTimeline) || apiTimeline.length === 0) return [];
        const t0Api = apiTimeline[0]?.timestamp ?? 0;

        return apiTimeline.map((point) => ({
            timeReal: point.timestamp,
            timeElapsed: (point.timestamp - t0Api) / 3600,
            color:
                point.hex ||
                `rgb(${point.r ?? 0},${point.g ?? 0},${point.b ?? 0})`,
        }));
    }, [apiTimeline]);

    const t0 = data[0]?.timeReal ?? 0;

    const realTicks = useMemo(() => {
        if (!data.length) return [];
        const xs = data.map((r) => r.timeReal);
        const min = Math.min(...xs);
        const max = Math.max(...xs);
        const d0 = new Date(min * 1000);
        d0.setMinutes(0, 0, 0);

        const out = [];
        let cur = d0.getTime() / 1000;
        while (cur <= max) {
            out.push(cur);
            cur += 3600;
        }
        return out;
    }, [data]);

    const elapsedTicks = useMemo(() => {
        if (!data.length) return [0];
        const es = data.map((r) => r.timeElapsed);
        const maxE = Math.floor(Math.max(...es));
        const ticks = Array.from({ length: maxE + 1 }, (_, i) => i);
        if (ticks.length > 0 && ticks[0] !== 0) ticks.unshift(0);
        return ticks;
    }, [data]);

    const dayBreaksElapsed = useMemo(() => {
        if (!data.length) return [];
        const xs = data.map((r) => r.timeReal);
        const firstMid = (() => {
            const d = new Date(xs[0] * 1000);
            d.setHours(24, 0, 0, 0);
            return Math.floor(d.getTime() / 1000);
        })();

        const arr = [];
        for (let ts = firstMid; ts <= xs.at(-1); ts += 86400) {
            arr.push((ts - t0) / 3600);
        }
        return arr;
    }, [data, t0]);

    if (data.length < 2) {
        return (
            <Card
                sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: height + (isMobile ? 120 : 150),
                    borderRadius: 3,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                }}
            >
                <Typography color="text.secondary">
                    Dados de cor indisponiveis no backend para este grafico.
                </Typography>
            </Card>
        );
    }

    return (
        <Card
            sx={{
                p: { xs: 1, sm: 2 },
                borderRadius: 3,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            }}
        >
            <ResponsiveContainer
                width="100%"
                height={height + (isMobile ? 120 : 150)}
            >
                <ComposedChart
                    data={data}
                    margin={{ top: 30, right: 5, bottom: 30, left: 5 }}
                >
                    <CartesianGrid
                        vertical={false}
                        stroke={theme.palette.divider}
                        strokeDasharray="3 3"
                    />
                    <YAxis yAxisId="hidden" hide />

                    <XAxis
                        xAxisId="real"
                        dataKey="timeReal"
                        type="number"
                        domain={['dataMin', 'dataMax']}
                        ticks={realTicks}
                        tickFormatter={formatRealHour}
                        orientation="top"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
                        label={{
                            value: 'Hora real',
                            position: 'top',
                            dy: -10,
                            fontSize: 12,
                            fill: theme.palette.text.secondary,
                        }}
                    />

                    <XAxis
                        xAxisId="elapsed"
                        dataKey="timeElapsed"
                        type="number"
                        domain={['dataMin', 'dataMax']}
                        ticks={elapsedTicks}
                        tickFormatter={(v) => `${v.toFixed(0)}h`}
                        orientation="bottom"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
                        label={{
                            value: 'Horas decorridas',
                            position: 'insideBottom',
                            dy: 10,
                            fontSize: 12,
                            fill: theme.palette.text.secondary,
                        }}
                    />

                    {data.map((entry, idx) => (
                        <ReferenceLine
                            key={`blk-${idx}`}
                            xAxisId="elapsed"
                            yAxisId="hidden"
                            x={entry.timeElapsed}
                            stroke={entry.color}
                            strokeWidth={height}
                        />
                    ))}

                    {dayBreaksElapsed.map((hr, i) => (
                        <ReferenceLine
                            key={`day-${i}`}
                            xAxisId="elapsed"
                            yAxisId="hidden"
                            x={hr}
                            stroke={theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.4)' : '#333'}
                            strokeDasharray="3 3"
                            strokeWidth={2}
                            label={{
                                value: dayjs
                                    .unix(t0 + hr * 3600)
                                    .format('DD/MM/YYYY'),
                                position: 'top',
                                dy: -20,
                                fontSize: 10,
                                fill: theme.palette.text.secondary,
                            }}
                        />
                    ))}
                </ComposedChart>
            </ResponsiveContainer>
        </Card>
    );
}

export default React.memo(ColorTimelineChart);
